import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'
import { useKillRaceHistory } from '../api/useKillRaceHistory'
import { KillRaceLog } from '../types'
import { formatDate, formatSpan, formatUsd, rankTone, toNumber } from '../utils/killrace'
import { EmptyBlock, ErrorBlock, GoArrow, Panel, Skeleton } from './KillRaceUI'

const COLUMNS = ['Race', 'Rank', 'Kills', 'Sessions', 'K/D', 'Earned', '']

/**
 * The viewer's last ten races.
 *
 * Headed by the Results tab rather than by a `SectionHeading` of its own — this is one branch of
 * `KillRaceBrowser`, not a band the page stacks underneath the races.
 */
export const HistoryPanel = () => {
    const { data, isLoading, isError, refetch } = useKillRaceHistory()
    const rows: KillRaceLog[] = Array.isArray(data) ? data : []

    if (isLoading) {
        return (
            <Panel className='flex flex-col gap-3 p-4'>
                {Array.from({ length: 4 }).map((_, i) => (
                    <Skeleton key={i} className='h-12 w-full' />
                ))}
            </Panel>
        )
    }

    if (isError) {
        return (
            <ErrorBlock title="Couldn't load your results" onRetry={refetch}>
                Your past results are still recorded. Only this view failed to load.
            </ErrorBlock>
        )
    }

    if (rows.length === 0) {
        return (
            <EmptyBlock title='No races played yet'>
                You have not placed in a kill race yet. Every kill you get while one is live enters you automatically,
                so there is nothing to sign up for.
            </EmptyBlock>
        )
    }

    return (
        <Panel className='p-3 md:p-4'>
            {/* Desktop: a real table, so the columns line up down the page. */}
            <table className='hidden w-full border-separate border-spacing-y-2 lg:table'>
                <thead>
                    <tr>
                        {COLUMNS.map((column, i) => (
                            <th
                                key={column || i}
                                className={twMerge(
                                    'px-4 pb-2 text-2xs font-bold uppercase tracking-[0.2em] text-white/35',
                                    i === 0 ? 'text-left' : 'text-right'
                                )}
                            >
                                {column}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((entry, i) => (
                        <HistoryRow key={entry.uuid ?? i} entry={entry} />
                    ))}
                </tbody>
            </table>

            {/* Mobile: one card per race — a 7-column table on a phone
                is either unreadable or a horizontal scroll nobody finds. */}
            <div className='flex flex-col gap-2.5 lg:hidden'>
                {rows.map((entry, i) => (
                    <HistoryCard key={entry.uuid ?? i} entry={entry} />
                ))}
            </div>
        </Panel>
    )
}

const RankBadge = ({ rank }: { rank?: number }) => {
    const tone = rankTone(rank)
    return (
        <span
            className={twMerge(
                'inline-flex h-8 min-w-[2rem] items-center justify-center border px-2 font-black tabular-nums',
                tone.border,
                tone.text
            )}
        >
            {rank ?? '—'}
        </span>
    )
}

const Earned = ({ value }: { value: unknown }) => (
    <span className={twMerge('font-black tabular-nums', toNumber(value) > 0 ? 'text-[#00FF90]' : 'text-white/30')}>
        {formatUsd(value)}
    </span>
)

const HistoryRow = ({ entry }: { entry: KillRaceLog }) => (
    <tr className='group bg-white/[0.03] transition-colors hover:bg-white/[0.07]'>
        <td className='px-4 py-2.5'>
            <div className='flex flex-col gap-0.5'>
                <span className='text-sm font-bold uppercase tracking-wide'>{formatDate(entry.begin)}</span>
                <span className='text-2xs uppercase tracking-[0.15em] text-white/35'>
                    {formatSpan(entry.begin, entry.end)}
                </span>
            </div>
        </td>
        <td className='px-4 py-2.5 text-right'>
            <RankBadge rank={entry.rank} />
        </td>
        <td className='px-4 py-2.5 text-right font-bold tabular-nums'>{entry.kills}</td>
        <td className='px-4 py-2.5 text-right font-bold tabular-nums text-white/50'>{entry.sessions}</td>
        <td className='px-4 py-2.5 text-right font-bold tabular-nums text-white/50'>{entry.kill_death_ratio}</td>
        <td className='px-4 py-2.5 text-right'>
            <Earned value={entry.earnings} />
        </td>
        <td className='px-4 py-2.5 text-right'>
            <Link to={`/kill-race/info/${entry.uuid}`} className='group inline-block'>
                <GoArrow />
            </Link>
        </td>
    </tr>
)

const HistoryCard = ({ entry }: { entry: KillRaceLog }) => (
    <Link
        to={`/kill-race/info/${entry.uuid}`}
        className='group flex flex-col gap-2 border border-white/10 bg-white/[0.03] px-3 py-2.5 transition-colors hover:bg-white/[0.07]'
    >
        <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-3'>
                <RankBadge rank={entry.rank} />
                <div className='flex flex-col gap-0.5'>
                    <span className='text-sm font-bold uppercase tracking-wide'>{formatDate(entry.begin)}</span>
                    <span className='text-2xs uppercase tracking-[0.15em] text-white/35'>
                        {formatSpan(entry.begin, entry.end)}
                    </span>
                </div>
            </div>
            <Earned value={entry.earnings} />
        </div>
        <div className='grid grid-cols-3 gap-2 border-t border-white/10 pt-2 text-center'>
            {[
                ['Kills', entry.kills],
                ['Sessions', entry.sessions],
                ['K/D', entry.kill_death_ratio],
            ].map(([label, value]) => (
                <div key={label as string} className='flex flex-col gap-1'>
                    <span className='text-2xs uppercase tracking-[0.2em] text-white/35'>{label}</span>
                    <span className='font-black tabular-nums'>{value}</span>
                </div>
            ))}
        </div>
    </Link>
)
